import { Injectable } from '@angular/core';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { reconnect, signMessage, watchAccount, watchConnections } from '@wagmi/core';
import { environment } from 'src/environments/environment';
import { polygon, polygonAmoy } from '@reown/appkit/networks';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';


export interface WalletState {
  walletAddress: string | null;
  chainId: number | null;
  connected: boolean;
  network: string | null;
  unsupportedChain: boolean;
  walletProvider?: 'metamask' | 'walletconnect' | string | null;
}
@Injectable({
  providedIn: 'root'
})

export class AppkitService {
  public adapter!: WagmiAdapter;
  private appKitInstance: any;
  private _walletState = new BehaviorSubject<WalletState>({
    walletAddress: null,
    chainId: null,
    connected: false,
    network: null,
    unsupportedChain: false,
    walletProvider: ""
  });
  public walletState$: Observable<WalletState> = this._walletState.asObservable();

  constructor(private storageService: StorageService) {
    this.initAppKit();
  }

  public async initAppKit() {
    try {
    const metadata = {
      name: 'Real World',
      description: 'RealWorld.fi - Market',
      url: environment.WEB_SITE_URL,
      icons: ['https://avatars.githubusercontent.com/u/37784886'],
      // redirect: {
      //   native: environment.MOBILE_APP_SCHEME,     // e.g. 'myapp://'
      //   universal: environment.WEB_SITE_URL + '/walletredirect'
      // }
  }
    const networks: any = environment.DEFAULT_NETWORK == '137' ? [polygon] : [polygonAmoy];

    const { DEFAULT_NETWORK } = environment;
    const envRpcUrls = (environment as any)[DEFAULT_NETWORK]?.PROVIDERS || [];
    console.log('envRpcUrls', envRpcUrls);

    // Convert hexadecimal to decimal value
    // const decimalChainId = parseInt(DEFAULT_NETWORK, 16);

    // Build the eip155 key
    const chainKey = `eip155:${environment.DEFAULT_NETWORK}`;

    const customRpcUrls = {
        [chainKey]: envRpcUrls.map((url: string) => ({ url })),
    };

    this.adapter = new WagmiAdapter({
      projectId: environment.WALLET_CONNECT_PROJECT_ID,
      networks,
      customRpcUrls
    });
    console.log('init appkit instance',  this.adapter);
    this.appKitInstance = await createAppKit({
      adapters: [ this.adapter ],
      networks,
      defaultNetwork: networks,
      metadata,
      projectId: environment.WALLET_CONNECT_PROJECT_ID,
      features: { analytics: false, socials: false, email: false },
      themeVariables: {
        "--w3m-color-mix": "#063f2dff",
        "--w3m-color-mix-strength": 40,
        "--w3m-accent": "#06402dff", 
      },
      enableReconnect: true,
      allowUnsupportedChain: false,
      enableNetworkSwitch: false,
    });

    watchAccount(this.adapter.wagmiConfig, {
      onChange: async (acct: any) => {
        console.log('acct---', acct);

        const connected = acct.isConnected === true;
        const walletAddress = connected ? acct.address : null;
        const chainId = connected ? acct.chainId : null;
        const unsupportedChain = connected 
            ? (environment.DEFAULT_NETWORK !== acct.chainId.toString()) 
            : false;

        let walletProvider: string = connected ? acct.connector.id : null;
        if (walletProvider === 'io.metamask') {
          walletProvider = 'metamask';
        } else if (walletProvider === 'walletConnect') {
          walletProvider = 'walletconnect';
        }

        this._walletState.next({
          walletAddress,
          chainId,
          connected,
          network: connected ? (environment as any)[environment.DEFAULT_NETWORK].CHAIN_NETWORK : null,
          unsupportedChain,
          walletProvider
        });
 // Persist the store (wagmi_new_store) in localStorage if you like
        if (connected) {
            const connectedNetwork = connected
              ? (environment as any)[chainId?.toString?.() || environment.DEFAULT_NETWORK]?.CHAIN_NETWORK ||
                (environment as any)[environment.DEFAULT_NETWORK]?.CHAIN_NETWORK ||
                null
              : null;
            const newStore = {
              account: walletAddress,
              address: walletAddress,
              chainId,
              isConnected: connected,
              network: connectedNetwork,
              provider: walletProvider,
              state: {
                data: {
                  account: walletAddress,
                  address: walletAddress,
                  network: connectedNetwork,
                  chainId,
                  provider: walletProvider,
                  chain: {
                    id: chainId,
                    unsupported: unsupportedChain
                  }
                }
              }
            };
          this.storageService.setItem('wagmi_new_store', JSON.stringify(newStore));
          this.storageService.setItem('wagmi_connected', 'true');
          } else {
            
            this.storageService.setItem('wagmi_connected', 'false');
            if (!acct.isConnected) {
              this._walletState.next({
                walletAddress: null,
                chainId: null,
                connected: false,
                network: null,
                unsupportedChain: false,
                walletProvider: null
              });
              return;
            }
        }
      }
    });

    console.log('Reconnecting wallet if possible...', this.adapter.wagmiConfig);
    // Attempt to auto-reconnect
    await reconnect(this.adapter.wagmiConfig);

    } catch (err) {
      console.error("WalletService.init error:", err);
    }
  }

  public async connectWallet(): Promise<void> {
    console.log('this.adapter',this.adapter);
    if (!this.adapter) {
      throw new Error('Wallet adapter not initialised');
    }
    await this.adapter.connect(this.appKitInstance);
    // After connect, account watcher will update state
  }

  public async signMessage(message: string): Promise<string> {
    const current = this._walletState.value;
    if (!current.connected || !current.walletAddress) {
      throw new Error('Wallet not connected');
    }

    if (!this.adapter) {
      throw new Error('Wallet service not yet initialized');
    }

    return await signMessage(this.adapter.wagmiConfig, {
      account: current.walletAddress as `0x${string}`,
      message
    });
  }

  public getWalletState(): WalletState {
    return this._walletState.value;
  }
}
